import { createStore, createHook, createActionsHook } from 'react-sweet-state';

// Shared open/close state for the convert-to-markdown blocking dialogs.
// Two flavours live here:
//
//  - `isLossyDialogOpen` — confirmation dialog shown when the doc is
//    lossy (rich content that won't survive markdown round-trip). User
//    can confirm and proceed.
//  - `syncBlockCount` (> 0 ⇔ open) — informational blocker shown when
//    the doc contains synced blocks. No "proceed" action — the user
//    must unlink them in the editor first.
//
// State lives in a sweet-state store so multiple call sites (header
// overflow menu, title `.md`-suffix gesture, future entry points) all
// drive the same dialog instances. The dialogs themselves are rendered
// in one place (currently `MoreActions`).
//
// Single global flags are fine: only one Live page editor is mounted at
// a time. If that ever changes, key by contentId.

// `syncBlockCount > 0` IS the synced-block blocker's open state — no
// separate boolean (would just risk drifting out of sync with the count).
type MarkdownLossyDialogState = {
	isLossyDialogOpen: boolean;
	syncBlockCount: number;
};

const initialState: MarkdownLossyDialogState = {
	isLossyDialogOpen: false,
	syncBlockCount: 0,
};

export const MarkdownLossyDialogStore = createStore({
	initialState,
	actions: {
		// Each `open*` action also clears the *other* dialog's open state
		// (`isLossyDialogOpen` ↔ `syncBlockCount`) so two entry points racing
		// can't stack both modals.
		openLossyDialog:
			() =>
			({ setState }) => {
				setState({ isLossyDialogOpen: true, syncBlockCount: 0 });
			},
		closeLossyDialog:
			() =>
			({ setState, getState }) => {
				if (getState().isLossyDialogOpen) {
					setState({ isLossyDialogOpen: false });
				}
			},
		openSyncBlockBlocker:
			(count: number) =>
			({ setState }) => {
				setState({ syncBlockCount: count, isLossyDialogOpen: false });
			},
		closeSyncBlockBlocker:
			() =>
			({ setState, getState }) => {
				if (getState().syncBlockCount > 0) {
					setState({ syncBlockCount: 0 });
				}
			},
	},
	name: 'MarkdownLossyDialogStore',
});

export const useMarkdownLossyDialogState = createHook(MarkdownLossyDialogStore);
export const useMarkdownLossyDialogActions = createActionsHook(MarkdownLossyDialogStore);
